/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Typography, TextField, InputAdornment,
    alpha, Skeleton, IconButton, Tooltip, Button, MenuItem, Stack,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { SYSTEM_FORM_TYPES } from 'src/pages/superAdmin/ConsentV2/consentConstants';
import { common } from 'src/theme/palette';
import FormQuickPreviewDrawer from './FormQuickPreviewDrawer';

const VALID_FORM_TYPES = SYSTEM_FORM_TYPES.map((t) => t.value);

// ── Color mapping per form type ─────────────────────────────────────────────
const TYPE_COLORS = {
    CONSENT: '#078DEE',
    MEDICAL_HISTORY: '#FF5630',
    SURVEY: '#855CF8',
    PATIENT_INTAKE: '#00AB55',
    REFERRAL: '#FFAB00',
    TREATMENT_PLAN: '#FF3030',
    OTHER: '#637381',
};

const getTypeColor = (formType) => TYPE_COLORS[formType] || '#637381';

const getTypeLabel = (formType) => {
    const found = SYSTEM_FORM_TYPES.find((t) => t.value === formType);
    return found?.label || formType?.replace(/_/g, ' ') || 'Other';
};

/**
 * FormSelector — search + form-type filter dropdown + form list.
 *
 *   * Type filter: single MUI Select (replaces the previous scrolling Tabs strip).
 *   * Quick presets: top dropdown showing the 3 most recent presets.
 *   * Header buttons: "Save current as preset" + "Manage presets".
 */
const FormSelector = ({
    forms,
    formsLoading,
    selectedForms,
    onAdd,
    onRemove,
    practiceId,
    sequencePresets,
    onApplyPreset,
    onManagePresets,
    onSaveCurrentAsPreset,
}) => {
    const [search, setSearch] = useState('');
    const [activeType, setActiveType] = useState('ALL');
    const [quickPresetId, setQuickPresetId] = useState('');
    // Quick-preview drawer state — opens to the right of the dialog so the
    // wizard stays in context. ``previewForm`` is the slim list-row;
    // ``FormQuickPreviewDrawer`` fetches the full detail itself.
    const [previewForm, setPreviewForm] = useState(null);

    // ── Derive valid forms list ──────────────────────────────────────────────
    const formsList = useMemo(() => {
        const raw = Array.isArray(forms) ? forms : (forms?.results || []);
        return raw.filter((f) => VALID_FORM_TYPES.includes(f.form_type));
    }, [forms]);

    // ── Get types that actually have forms ───────────────────────────────────
    const activeTypes = useMemo(() => {
        const typeSet = new Set(formsList.map((f) => f.form_type));
        return SYSTEM_FORM_TYPES.filter((t) => typeSet.has(t.value));
    }, [formsList]);

    // ── Filter by active type + search ───────────────────────────────────────
    const filteredForms = useMemo(() => {
        let filtered = formsList;
        if (activeType !== 'ALL') {
            filtered = filtered.filter((f) => f.form_type === activeType);
        }
        if (search.trim()) {
            const q = search.toLowerCase().trim();
            filtered = filtered.filter(
                (f) =>
                    (f.name || '').toLowerCase().includes(q) ||
                    (f.description || '').toLowerCase().includes(q)
            );
        }
        return filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [formsList, activeType, search]);

    const selectedIds = useMemo(() => new Set(selectedForms.map((f) => f.id)), [selectedForms]);

    // ── Count per type for the Select labels ─────────────────────────────────
    const typeCounts = useMemo(() => {
        const counts = {};
        formsList.forEach((f) => {
            counts[f.form_type] = (counts[f.form_type] || 0) + 1;
        });
        return counts;
    }, [formsList]);

    const handleQuickPresetChange = (e) => {
        const id = e.target.value;
        setQuickPresetId(id);
        if (id) {
            onApplyPreset(id);
            // Reset to a neutral value so selecting the same preset twice still triggers
            setTimeout(() => setQuickPresetId(''), 50);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* ── Search + preset action buttons ─────────────────────────── */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                <TextField
                    size="small"
                    placeholder="Search forms..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Iconify icon="eva:search-fill" width={16} sx={{ color: '#919EAB' }} />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.25 }}>
                                    <Iconify icon="eva:close-fill" width={14} sx={{ color: '#919EAB' }} />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                    sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            fontSize: '13px',
                            '& fieldset': { borderColor: alpha('#919EAB', 0.24) },
                        },
                    }}
                />

                <Tooltip
                    title={selectedForms.length === 0 ? 'Select at least one form to save as a preset' : ''}
                    disableInteractive
                >
                    <span>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={selectedForms.length === 0}
                            onClick={onSaveCurrentAsPreset}
                            startIcon={<Iconify icon="solar:bookmark-bold" width={14} />}
                            sx={{
                                borderRadius: '10px',
                                textTransform: 'none', fontWeight: 600, fontSize: '12px',
                                px: 1.25, py: 0.65, minHeight: 36,
                                whiteSpace: 'nowrap',
                                borderColor: alpha(common.mintyZest, 0.3),
                                color: common.mintyZest,
                                '&:hover': {
                                    borderColor: common.mintyZest,
                                    bgcolor: alpha(common.mintyZest, 0.06),
                                },
                                '&.Mui-disabled': {
                                    borderColor: alpha('#919EAB', 0.24),
                                    color: '#919EAB',
                                },
                            }}
                        >
                            Save preset
                        </Button>
                    </span>
                </Tooltip>

                <Button
                    size="small"
                    variant="outlined"
                    onClick={onManagePresets}
                    startIcon={<Iconify icon="solar:settings-bold" width={14} />}
                    sx={{
                        borderRadius: '10px',
                        textTransform: 'none', fontWeight: 600, fontSize: '12px',
                        px: 1.25, py: 0.65, minHeight: 36,
                        whiteSpace: 'nowrap',
                        borderColor: alpha('#919EAB', 0.32),
                        color: '#637381',
                        '&:hover': {
                            borderColor: '#919EAB',
                            bgcolor: alpha('#919EAB', 0.06),
                        },
                    }}
                >
                    Manage
                </Button>
            </Stack>

            {/* ── Type filter + Quick presets row ────────────────────────── */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                <TextField
                    select
                    size="small"
                    value={activeType}
                    onChange={(e) => setActiveType(e.target.value)}
                    label="Form type"
                    sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '13px', bgcolor: '#fff' },
                        '& .MuiSelect-select': { py: 0.85 },
                        '& .MuiInputLabel-root': { fontSize: '12px' },
                    }}
                    inputProps={{ 'aria-label': 'Filter by form type' }}
                >
                    <MenuItem value="ALL" sx={{ fontSize: '13px' }}>
                        All ({formsList.length})
                    </MenuItem>
                    {activeTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value} sx={{ fontSize: '13px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box
                                    sx={{
                                        width: 8, height: 8, borderRadius: '50%',
                                        bgcolor: getTypeColor(type.value),
                                    }}
                                />
                                {type.label}
                                <Typography
                                    component="span"
                                    sx={{ ml: 0.5, fontSize: '11px', color: 'text.secondary' }}
                                >
                                    ({typeCounts[type.value] || 0})
                                </Typography>
                            </Box>
                        </MenuItem>
                    ))}
                </TextField>

                {sequencePresets.length > 0 && (
                    <TextField
                        select
                        size="small"
                        label="Quick preset"
                        value={quickPresetId}
                        onChange={handleQuickPresetChange}
                        sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '13px', bgcolor: '#fff' },
                            '& .MuiSelect-select': { py: 0.85 },
                            '& .MuiInputLabel-root': { fontSize: '12px' },
                        }}
                        inputProps={{ 'aria-label': 'Apply a saved preset' }}
                    >
                        <MenuItem value="" sx={{ fontSize: '13px', color: 'text.secondary' }}>
                            <em>Apply a preset…</em>
                        </MenuItem>
                        {sequencePresets.slice(0, 3).map((preset) => (
                            <MenuItem key={preset.id} value={preset.id} sx={{ fontSize: '13px' }}>
                                <Iconify
                                    icon="solar:playlist-minimalistic-bold"
                                    width={14}
                                    sx={{ mr: 0.75, color: '#078DEE' }}
                                />
                                {preset.name}
                                {preset.form_ids?.length > 0 && (
                                    <Typography
                                        component="span"
                                        sx={{ ml: 0.5, fontSize: '11px', color: 'text.secondary' }}
                                    >
                                        ({preset.form_ids.length})
                                    </Typography>
                                )}
                            </MenuItem>
                        ))}
                        {sequencePresets.length > 3 && (
                            <MenuItem
                                value=""
                                onClick={() => { onManagePresets(); }}
                                sx={{ fontSize: '12.5px', color: '#637381', borderTop: '1px solid', borderColor: 'divider' }}
                            >
                                See all {sequencePresets.length} presets…
                            </MenuItem>
                        )}
                    </TextField>
                )}
            </Stack>

            {/* ── Form list ──────────────────────────────────────────────── */}
            {/* Cap height so a practice with many forms doesn't push Practice
                fields + Channel & message off-screen. Scrolls internally. */}
            <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', maxHeight: 280 }}>
                {/* eslint-disable-next-line no-nested-ternary */}
                {formsLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {[1, 2, 3, 4].map((i) => (
                            <Skeleton key={i} variant="rounded" height={52} sx={{ borderRadius: '10px' }} />
                        ))}
                    </Box>
                ) : filteredForms.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Iconify icon="solar:document-text-bold" width={32} sx={{ color: '#C4CDD5', mb: 1 }} />
                        <Typography variant="body2" sx={{ color: '#919EAB', fontSize: '13px' }}>
                            {search ? 'No forms match your search' : 'No forms available'}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {filteredForms.map((form) => {
                            const isSelected = selectedIds.has(form.id);
                            const typeColor = getTypeColor(form.form_type);
                            return (
                                <Box
                                    key={form.id}
                                    onClick={() => !isSelected && onAdd(form)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.25,
                                        px: 1.5,
                                        py: 1,
                                        borderRadius: '10px',
                                        border: '1px solid',
                                        borderColor: isSelected ? alpha(common.mintyZest, 0.3) : alpha('#919EAB', 0.2),
                                        bgcolor: isSelected ? alpha(common.mintyZest, 0.04) : 'transparent',
                                        cursor: isSelected ? 'default' : 'pointer',
                                        transition: 'all 0.15s ease',
                                        '&:hover': isSelected ? {} : {
                                            borderColor: alpha(common.mintyZest, 0.4),
                                            bgcolor: alpha(common.mintyZest, 0.02),
                                        },
                                    }}
                                >
                                    {/* Type color bar */}
                                    <Box sx={{ width: 3, height: 28, borderRadius: 2, bgcolor: typeColor, flexShrink: 0 }} />

                                    {/* Form info */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px', color: '#212B36', lineHeight: 1.3 }} noWrap>
                                            {form.name}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                                            <Typography variant="caption" sx={{
                                                fontSize: '10px', fontWeight: 600, color: typeColor,
                                                textTransform: 'uppercase', letterSpacing: '0.3px',
                                            }}>
                                                {getTypeLabel(form.form_type)}
                                            </Typography>
                                            {form.sections?.length > 0 && (
                                                <Typography variant="caption" sx={{ fontSize: '10px', color: '#919EAB' }}>
                                                    {form.sections.length} section{form.sections.length !== 1 ? 's' : ''}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Quick preview — opens the structured drawer.
                                        Stops propagation so the surrounding card click
                                        (which adds to selection) doesn't fire. */}
                                    <Tooltip title="Preview form" arrow placement="left">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => { e.stopPropagation(); setPreviewForm(form); }}
                                            sx={{
                                                flexShrink: 0, width: 24, height: 24, color: '#C4CDD5',
                                                '&:hover': { color: '#078DEE', bgcolor: alpha('#078DEE', 0.08) },
                                            }}
                                            aria-label={`Preview ${form.name || 'form'}`}
                                        >
                                            <Iconify icon="eva:eye-outline" width={18} />
                                        </IconButton>
                                    </Tooltip>

                                    {/* Add/selected indicator */}
                                    {isSelected ? (
                                        <Iconify icon="eva:checkmark-circle-2-fill" width={20} sx={{ color: common.mintyZest, flexShrink: 0 }} />
                                    ) : (
                                        <Tooltip title="Add to selection" arrow placement="left">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => { e.stopPropagation(); onAdd(form); }}
                                                sx={{
                                                    flexShrink: 0, width: 24, height: 24, color: '#C4CDD5',
                                                    '&:hover': { color: common.mintyZest, bgcolor: alpha(common.mintyZest, 0.08) },
                                                }}
                                            >
                                                <Iconify icon="eva:plus-circle-outline" width={18} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>

            {/* Quick preview drawer — rendered at the section root so the
                MUI Drawer slides in from the viewport edge, not from inside
                the bounded form-list scroll area. */}
            {previewForm && (
                <FormQuickPreviewDrawer
                    open={Boolean(previewForm)}
                    onClose={() => setPreviewForm(null)}
                    practiceId={practiceId}
                    form={previewForm}
                    typeColor={getTypeColor(previewForm.form_type)}
                    typeLabel={getTypeLabel(previewForm.form_type)}
                    isSelected={selectedIds.has(previewForm.id)}
                    onAdd={onAdd}
                    onRemove={onRemove}
                />
            )}
        </Box>
    );
};

FormSelector.propTypes = {
    forms: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
    formsLoading: PropTypes.bool,
    selectedForms: PropTypes.array.isRequired,
    onAdd: PropTypes.func.isRequired,
    onRemove: PropTypes.func,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    sequencePresets: PropTypes.array,
    onApplyPreset: PropTypes.func,
    onManagePresets: PropTypes.func,
    onSaveCurrentAsPreset: PropTypes.func,
};

FormSelector.defaultProps = {
    formsLoading: false,
    onRemove: undefined,
    sequencePresets: [],
    onApplyPreset: () => {},
    onManagePresets: () => {},
    onSaveCurrentAsPreset: () => {},
};

export default FormSelector;
